// Unified hover-action primitive used across the create-project and
// new-team-change scaffolds. One button, one chrome, three kinds —
// `add`, `edit`, `delete`. Lives at opacity 0; the parent (any element
// with the `has-hover-actions` class) flips it to opacity 1 on hover
// and focus-within. A CSS pseudo-element renders the tooltip so the
// label appears instantly rather than waiting on the browser's native
// `title=` delay.

import type { ReactNode } from 'react';

export type HoverActionKind =
  | 'add'
  | 'edit'
  | 'delete'
  | 'deprecate'
  | 'restore';

const ICONS: Record<HoverActionKind, ReactNode> = {
  add: (
    <svg
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.8'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden
    >
      <path d='M12 5v14' />
      <path d='M5 12h14' />
    </svg>
  ),
  edit: (
    <svg
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden
    >
      <path d='M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z' />
      <path d='m15 5 4 4' />
    </svg>
  ),
  delete: (
    <svg
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden
    >
      <path d='M3 6h18' />
      <path d='M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2' />
      <path d='M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6' />
    </svg>
  ),
  deprecate: (
    // Lucide-style strikethrough — visually matches the diagonal-stripe
    // result the deprecated-cell pattern paints.
    <svg
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.7'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden
    >
      <path d='M16 4H9a3 3 0 0 0-2.83 4' />
      <path d='M14 12a4 4 0 0 1 0 8H6' />
      <line x1='4' x2='20' y1='12' y2='12' />
    </svg>
  ),
  restore: (
    // Counter-clockwise undo arrow — "bring this back".
    <svg
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.7'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden
    >
      <path d='M3 7v6h6' />
      <path d='M21 17a9 9 0 0 0-9-9 9.74 9.74 0 0 0-7 3L3 13' />
    </svg>
  ),
};

export function HoverAction({
  kind,
  onClick,
  label,
}: {
  kind: HoverActionKind;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type='button'
      className={`snap-hover-action snap-hover-action--${kind}`}
      data-tooltip={label}
      aria-label={label}
      // Keep focus on the adjacent edit input: in edit mode these
      // buttons sit next to a focused <input> whose onBlur commits the
      // rename. preventDefault on mousedown stops the click from blurring
      // the input first (which would commit-then-act).
      onMouseDown={e => e.preventDefault()}
      onClick={e => {
        // stopPropagation so the action button doesn't also trigger a
        // surrounding click-to-edit affordance (e.g. EditableLabel's
        // outer span listener).
        e.stopPropagation();
        onClick();
      }}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.stopPropagation();
        }
      }}
    >
      {ICONS[kind]}
    </button>
  );
}

export function HoverActionGroup({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  const classes = ['snap-hover-action-group'];
  if (className) classes.push(className);
  return <div className={classes.join(' ')}>{children}</div>;
}
